import { Button } from '@mui/material';
import { DashboardDto, DashboardMutatePayload } from '@thingslog/repositories';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  createDefaultCustomTimespan,
  createDefaultPredefinedTimespan,
  TimespanPickerFields,
  TimespanPickerMode,
} from '../timespan';

interface Props {
  dashboard: DashboardDto;
  disabled?: boolean;
  onSubmit: (payload: DashboardMutatePayload) => void;
  onCancel: () => void;
}

export const DashboardTimeframeForm: FC<Props> = ({
  dashboard,
  disabled = false,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const { control, handleSubmit, setValue, watch } = useForm<DashboardMutatePayload>({
    mode: 'onChange',
    defaultValues: {
      name: dashboard.name,
      period: dashboard.period,
      colsDesktop: dashboard.colsDesktop,
      colsMobile: dashboard.colsMobile,
      layoutSwitchBreakpointPx: dashboard.layoutSwitchBreakpointPx,
    },
  });

  const periodType = watch('period.type');

  const handlePeriodTypeChange = (nextType: TimespanPickerMode): void => {
    if (nextType === 'none') {
      return;
    }

    setValue(
      'period',
      nextType === 'predefined' ? createDefaultPredefinedTimespan() : createDefaultCustomTimespan(),
      { shouldDirty: true, shouldValidate: true }
    );
    setValue('period.type', nextType, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <form
      className="flex min-w-0 flex-col gap-4 p-4"
      onSubmit={handleSubmit((payload: DashboardMutatePayload): void => onSubmit(payload))}
    >
      <div data-dashboard-tour="dashboard-form-period">
        <TimespanPickerFields<DashboardMutatePayload>
          control={control}
          mode={periodType}
          modeName="period.type"
          title={t('dashboard_dashboard_time_period', 'Dashboard Time Period')}
          description={t(
            'dashboard_dashboard_time_period_description',
            'Controls the default timeframe used by the dashboard and by widgets that inherit from it.'
          )}
          predefinedName="period.name"
          fromDateName="period.fromDate"
          toDateName="period.toDate"
          className="flex min-w-0 flex-col gap-3"
          tourTargetBase="dashboard-timespan"
          disabled={disabled}
          onModeChange={handlePeriodTypeChange}
        />
      </div>

      <div className="flex justify-end gap-2 border-0 border-t border-solid border-gray-200 pt-4">
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" disableElevation disabled={disabled}>
          {t('dashboard_save_period', 'Save Period')}
        </Button>
      </div>
    </form>
  );
};
